import { useId, useRef, useState } from 'react'
import { endpoints } from '@/shared/api/endpoints'
import { Icon } from '@/shared/ui/Icon'

/**
 * Photo justificative : capture, dépôt, puis chemin rendu à l'appelant.
 *
 * Le dépôt se fait en deux temps — la pièce d'abord, le relevé ensuite. Le
 * serveur vérifie que la pièce existe, qu'elle appartient à celui qui la
 * présente et qu'elle n'a jamais servi : un chemin fabriqué côté console est
 * refusé, et c'est voulu. Une photo d'index n'est pas une illustration, c'est
 * ce qui rend un relevé opposable.
 *
 * Tant que le dépôt n'a pas répondu, aucun chemin n'est rendu : le bouton
 * d'envoi du formulaire reste donc inopérant, plutôt que de laisser croire à
 * une preuve qui n'existe pas.
 */
export function FieldPhoto({
  label,
  help,
  error,
  path,
  clientReference,
  onUploaded,
}: {
  label: string
  help?: string
  error?: string
  /** Chemin rendu par le serveur, ou `null` tant que rien n'est déposé. */
  path: string | null
  /** Référence du relevé en cours, pour qu'un rejeu retrouve sa pièce. */
  clientReference?: string
  onUploaded: (path: string | null) => void
}) {
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)

  async function take(file: File | undefined) {
    if (!file) return
    setBusy(true)
    setFailure(null)
    onUploaded(null)
    try {
      const r = await endpoints.attachments.upload(file, clientReference)
      setName(r.data.original_name)
      onUploaded(r.data.path)
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Le dépôt a échoué.')
    } finally {
      setBusy(false)
      // Le champ est vidé pour qu'une seconde prise du même fichier déclenche
      // bien un nouvel événement.
      if (input.current) input.current.value = ''
    }
  }

  const message = failure ?? error
  const tone = message ? 'var(--bad)' : path ? 'var(--good)' : undefined

  return (
    <div className="field" style={{ gridColumn: '1 / -1' }}>
      <span className="field__label">
        {label} <span style={{ color: 'var(--bad)' }}>*</span>
      </span>

      <input
        ref={input}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        // Sur un téléphone, ouvre l'appareil arrière plutôt que la galerie :
        // la photo doit être prise devant le compteur, pas retrouvée.
        capture="environment"
        hidden
        onChange={(e) => void take(e.target.files?.[0])}
      />

      <button
        type="button"
        onClick={() => input.current?.click()}
        className="shot"
        disabled={busy}
        style={{
          cursor: busy ? 'progress' : 'pointer',
          width: '100%',
          font: 'inherit',
          borderColor: tone,
          color: tone,
        }}
      >
        <Icon name={path ? 'check' : 'camera'} />
        <br />
        {busy
          ? 'Envoi de la photo…'
          : path
            ? `Photo déposée${name ? ` — ${name}` : ''}`
            : 'Prendre la photo'}
      </button>

      {message ? (
        <span className="small" style={{ color: 'var(--bad)' }}>
          {message}
        </span>
      ) : help ? (
        <span className="small muted">{help}</span>
      ) : null}
    </div>
  )
}
